package diana.soleil.design.iraniancanadienbazzar.feature.chat

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.MarketplaceRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Inbox state: the signed-in user's threads, split into accepted [conversations] and message [requests]. */
data class InboxUiState(
    val loading: Boolean = true,
    val conversations: List<Conversation> = emptyList(),
    /** Pending DM requests for the signed-in user (message-requests model) — shown in a separate rail. */
    val requests: List<Conversation> = emptyList(),
    /** Community channels of followed businesses (newest post first) — the "Channels" section. */
    val channels: List<diana.soleil.design.iraniancanadienbazzar.core.model.Channel> = emptyList(),
    val query: String = "",
    /** Guest/signed-out — the route sends the user to sign-in instead of an empty inbox. */
    val signInRequired: Boolean = false,
    val error: Boolean = false,
) {
    val filtered: List<Conversation>
        get() = if (query.isBlank()) conversations
        else conversations.filter { it.businessName.contains(query.trim(), ignoreCase = true) }
}

/** Streams the inbox (Stitch `f13a78d4`) from [ChatRepository]. */
@OptIn(ExperimentalCoroutinesApi::class)
class InboxViewModel(
    private val chatRepository: ChatRepository,
    private val notificationRepository: NotificationRepository,
    blockRepository: BlockRepository,
    followRepository: diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository,
    channelRepository: diana.soleil.design.iraniancanadienbazzar.core.data.ChannelRepository,
) : ViewModel() {

    private val queryFlow = MutableStateFlow("")
    // Bumped by retry() to re-subscribe the conversation stream after a load failure.
    private val reloadKey = MutableStateFlow(0)

    // Channels of followed businesses. Additive decoration — any failure just hides the section.
    private val channelsFlow = followRepository.observeFollowing()
        .flatMapLatest { following ->
            flowOf(
                when (following) {
                    is DataResult.Success ->
                        (channelRepository.channelsFor(following.data) as? DataResult.Success)?.data.orEmpty()
                    is DataResult.Failure -> emptyList()
                },
            )
        }
        .catch { emit(emptyList()) }

    val state: StateFlow<InboxUiState> =
        combine(
            reloadKey.flatMapLatest { chatRepository.observeConversations() },
            notificationRepository.observeNotifications(),
            blockRepository.observeBlockedUids(),
            channelsFlow,
            queryFlow,
        ) { conversations, notifications, blocked, channels, query ->
            when {
                conversations is DataResult.Success -> {
                    // Unread counts are a DECORATION on the thread list — a transient failure of
                    // the notifications stream must degrade to "no badges", never blank a healthy
                    // conversation list into the error screen.
                    val unreadByConversation = (notifications as? DataResult.Success)?.data.orEmpty()
                        .filter { !it.read && it.conversationId != null }
                        .groupingBy { it.conversationId }
                        .eachCount()
                    val me = chatRepository.currentUid
                    val visible = conversations.data
                        // Drop threads with a blocked counterpart. The caller can never be in
                        // their own block list (self-block is rejected), so "any participant
                        // blocked" means exactly "the OTHER participant is blocked".
                        .filter { conversation -> conversation.participantUids.none { it in blocked } }
                        .map { conversation ->
                            conversation.copy(unreadCount = unreadByConversation[conversation.id] ?: 0)
                        }
                    // A pending DM request (I was invited, haven't accepted) goes to the Requests rail;
                    // everything else (accepted DMs, business/item threads) is the main inbox.
                    val (requests, accepted) = visible.partition { me != null && it.isRequestFor(me) }
                    InboxUiState(
                        loading = false,
                        conversations = accepted,
                        requests = requests,
                        channels = channels,
                        query = query,
                    )
                }
                conversations is DataResult.Failure -> InboxUiState(
                    loading = false,
                    signInRequired = conversations.error is AppError.Unauthorized,
                    error = conversations.error !is AppError.Unauthorized,
                )
                else -> InboxUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), InboxUiState())

    fun onQueryChange(query: String) {
        queryFlow.value = query
    }

    /** Re-subscribes the conversation stream after a load failure (wired to the error-state Retry). */
    fun retry() {
        reloadKey.value += 1
    }

    fun markConversationRead(conversationId: String) {
        viewModelScope.launch { notificationRepository.markConversationRead(conversationId) }
    }
}

/** Lifecycle of a "report this user" submission (mirrors the feed's post-report pattern). */
enum class ChatReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

/** Conversation state: header identity + live messages + composer/send progress + trust/safety. */
data class ConversationUiState(
    val loading: Boolean = true,
    val businessName: String = "",
    val messages: List<Message> = emptyList(),
    val sending: Boolean = false,
    /** The last send didn't go through (non-auth failure) — the screen keeps the draft and shows an inline error. */
    val sendFailed: Boolean = false,
    /** Member-created group thread — bubbles show sender names; header offers Leave. */
    val isGroup: Boolean = false,
    /** Display-name snapshots for labeling group message senders. */
    val senderNames: Map<String, String> = emptyMap(),
    val signInRequired: Boolean = false,
    val error: Boolean = false,
    /**
     * The other participant's uid (`participantUids` minus the signed-in uid), once the conversation
     * doc is loaded. Null for guests or while resolving — the header hides report/block until known.
     */
    val counterpartyUid: String? = null,
    /** True for marketplace buyer↔seller threads — drives the safety note + quick replies. */
    val isItemThread: Boolean = false,
    val reportStatus: ChatReportStatus = ChatReportStatus.Idle,
    /** The counterparty was just blocked — the thread is hidden from the inbox, so the route backs out. */
    val blockCompleted: Boolean = false,
    /**
     * True when this is a DM message REQUEST the signed-in user hasn't accepted yet: the composer is
     * replaced by Accept / Decline. Sending is gated until they accept.
     */
    val isPendingRequest: Boolean = false,
    /** The request was declined (thread deleted) — the route backs out. */
    val requestDeclined: Boolean = false,
)

/**
 * Drives one thread (Stitch `c8110400`). Reached two ways: with an existing [conversationId] (from the
 * inbox) or with a [businessId] (from a business detail's Chat button) — the latter resolves the
 * business and creates the thread on first contact (`ensureConversation`). Guests get
 * [ConversationUiState.signInRequired] and are routed to sign-in.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ConversationViewModel(
    private val chatRepository: ChatRepository,
    private val businessRepository: BusinessRepository,
    private val notificationRepository: NotificationRepository,
    private val reportRepository: ReportRepository,
    private val blockRepository: BlockRepository,
    private val marketplaceRepository: MarketplaceRepository,
    private val conversationId: String?,
    private val businessId: String?,
) : ViewModel() {

    private val resolvedId = MutableStateFlow(conversationId)
    private val header = MutableStateFlow("")
    private val sending = MutableStateFlow(false)
    private val sendFailed = MutableStateFlow(false)
    private val gate = MutableStateFlow<AppError?>(null)
    /** The resolved thread doc — source of `participantUids`/`itemId` for the trust/safety UI. */
    private val conversation = MutableStateFlow<Conversation?>(null)
    private val reportStatus = MutableStateFlow(ChatReportStatus.Idle)
    private val blockCompleted = MutableStateFlow(false)
    private val requestDeclined = MutableStateFlow(false)

    private data class Safety(
        val convo: Conversation?,
        val report: ChatReportStatus,
        val blocked: Boolean,
        val declined: Boolean,
    )

    val state: StateFlow<ConversationUiState> =
        combine(
            resolvedId.flatMapLatest { id ->
                if (id == null) flowOf(DataResult.Success(emptyList()))
                else chatRepository.observeMessages(id)
            },
            header,
            combine(sending, sendFailed, gate) { isSending, didSendFail, gateError ->
                Triple(isSending, didSendFail, gateError)
            },
            combine(conversation, reportStatus, blockCompleted, requestDeclined) { convo, report, blocked, declined ->
                Safety(convo, report, blocked, declined)
            },
        ) { messages, businessName, sendState, safety ->
            val (isSending, didSendFail, gateError) = sendState
            val convo = safety.convo
            when {
                gateError is AppError.Unauthorized -> ConversationUiState(loading = false, signInRequired = true)
                gateError != null -> ConversationUiState(loading = false, error = true)
                else -> when (messages) {
                    is DataResult.Success -> {
                        val currentUid = chatRepository.currentUid
                        ConversationUiState(
                            loading = resolvedId.value == null,
                            businessName = businessName,
                            messages = messages.data,
                            sending = isSending,
                            sendFailed = didSendFail,
                            counterpartyUid = if (currentUid == null) null
                            else convo?.participantUids?.firstOrNull { it != currentUid },
                            isItemThread = convo?.itemId != null,
                            reportStatus = safety.report,
                            blockCompleted = safety.blocked,
                            isGroup = convo?.type == Conversation.TYPE_GROUP,
                            senderNames = convo?.participantNames.orEmpty(),
                            isPendingRequest = currentUid != null && convo?.isRequestFor(currentUid) == true,
                            requestDeclined = safety.declined,
                        )
                    }
                    is DataResult.Failure -> ConversationUiState(loading = false, error = true)
                }
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ConversationUiState())

    /** Accept a pending message request — adds the caller to the thread's accepted set. */
    fun acceptRequest() {
        val id = resolvedId.value ?: return
        viewModelScope.launch {
            when (val result = chatRepository.acceptConversation(id)) {
                is DataResult.Success ->
                    // Refresh the doc so `isPendingRequest` flips and the composer appears.
                    (chatRepository.getConversation(id) as? DataResult.Success)?.let { conversation.value = it.data }
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) gate.value = result.error
            }
        }
    }

    /**
     * Decline a pending request and back the route out. A declined DM deletes the thread; a
     * declined GROUP invite removes only the caller (the group lives on for its members).
     */
    fun declineRequest() {
        val id = resolvedId.value ?: return
        val isGroup = conversation.value?.type == Conversation.TYPE_GROUP
        viewModelScope.launch {
            val result = if (isGroup) chatRepository.leaveConversation(id) else chatRepository.declineConversation(id)
            when (result) {
                is DataResult.Success -> requestDeclined.value = true
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) gate.value = result.error
            }
        }
    }

    /** Leave a joined group (header action) — removes only the caller, then backs the route out. */
    fun leaveGroup() {
        val id = resolvedId.value ?: return
        viewModelScope.launch {
            when (val result = chatRepository.leaveConversation(id)) {
                is DataResult.Success -> requestDeclined.value = true
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) gate.value = result.error
            }
        }
    }

    init {
        resolve()
    }

    fun retry() {
        gate.value = null
        resolve()
    }

    fun send(text: String) {
        val id = resolvedId.value ?: return
        if (text.isBlank() || sending.value) return
        viewModelScope.launch {
            sending.value = true
            sendFailed.value = false
            when (val result = chatRepository.sendMessage(id, text)) {
                is DataResult.Success -> Unit
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) gate.value = result.error
                    else sendFailed.value = true
            }
            sending.value = false
        }
    }

    /** The user is typing again — drop the stale send error. */
    fun clearSendFailed() {
        sendFailed.value = false
    }

    /**
     * Files a moderation report against the thread counterparty; guests trip
     * [ChatReportStatus.SignInRequired] and the route bounces to sign-in.
     */
    fun report(reason: String) {
        val target = state.value.counterpartyUid ?: return
        if (reportStatus.value == ChatReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = ChatReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("user", target, reason)) {
                is DataResult.Success -> ChatReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) ChatReportStatus.SignInRequired
                    else ChatReportStatus.Failed
            }
        }
    }

    /** The report sheet was dismissed — clear its status for the next open. */
    fun resetReport() {
        reportStatus.value = ChatReportStatus.Idle
    }

    /**
     * Blocks [targetUid] — the thread vanishes from the inbox (block-list filter), so on success the
     * route navigates back. Guests bounce to sign-in via the gate; the target is never notified.
     */
    fun blockUser(targetUid: String) {
        viewModelScope.launch {
            // Item threads are titled after the item, not a person. Snapshot the seller's public
            // name when the target IS the seller; otherwise store no name — the blocked list shows
            // a localized fallback (the counterparty's users/{uid} doc is unreadable to clients).
            val itemId = conversation.value?.itemId
            val targetName = if (itemId == null) {
                state.value.businessName
            } else {
                (marketplaceRepository.getItem(itemId) as? DataResult.Success)
                    ?.data?.takeIf { it.sellerUid == targetUid }?.sellerName.orEmpty()
            }
            when (val result = blockRepository.block(targetUid, targetName)) {
                is DataResult.Success -> blockCompleted.value = true
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) gate.value = result.error
            }
        }
    }

    // ── unchanged below ──────────────────────────────────────────────────────────────────────────
    private fun resolve() {
        viewModelScope.launch {
            when {
                // Opened from the inbox: header identity from the conversation doc's name snapshot.
                conversationId != null -> when (val convo = chatRepository.getConversation(conversationId)) {
                    is DataResult.Success -> {
                        header.value = convo.data.businessName
                        conversation.value = convo.data
                        notificationRepository.markConversationRead(conversationId)
                    }
                    is DataResult.Failure -> gate.value = convo.error
                }
                businessId != null -> when (val business = businessRepository.getBusiness(businessId)) {
                    is DataResult.Success -> {
                        header.value = business.data.name
                        when (val ensured = chatRepository.ensureConversation(business.data)) {
                            is DataResult.Success -> {
                                resolvedId.value = ensured.data
                                notificationRepository.markConversationRead(ensured.data)
                                // Re-read the (possibly just-created) doc for participantUids —
                                // the header's report/block need the counterparty. Best-effort.
                                (chatRepository.getConversation(ensured.data) as? DataResult.Success)
                                    ?.let { conversation.value = it.data }
                            }
                            is DataResult.Failure -> gate.value = ensured.error
                        }
                    }
                    is DataResult.Failure -> gate.value = business.error
                }
                else -> gate.value = AppError.NotFound()
            }
        }
    }

}

/** New-message (user discovery) state: prefix search over public profiles + the started thread id. */
data class NewMessageUiState(
    val query: String = "",
    val results: List<PublicProfile> = emptyList(),
    val searching: Boolean = false,
    val signInRequired: Boolean = false,
    /** Set once a DM/group thread is created/resolved — the screen navigates into it, then clears this. */
    val openConversationId: String? = null,
    /** Group-creation mode: search rows toggle membership instead of opening a DM. */
    val groupMode: Boolean = false,
    val groupTitle: String = "",
    val groupMembers: List<PublicProfile> = emptyList(),
    val creatingGroup: Boolean = false,
)

/**
 * Drives the "New message" screen: debounced display-name search over `publicProfiles`, and starting a
 * DM (message request) with a tapped user via [ChatRepository.ensureDmConversation].
 */
class NewMessageViewModel(
    private val chatRepository: ChatRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(NewMessageUiState())
    val state: StateFlow<NewMessageUiState> = _state.asStateFlow()

    private var searchJob: Job? = null

    fun onQueryChange(query: String) {
        _state.value = _state.value.copy(query = query)
        searchJob?.cancel()
        if (query.trim().length < 2) {
            _state.value = _state.value.copy(results = emptyList(), searching = false)
            return
        }
        searchJob = viewModelScope.launch {
            delay(300) // debounce keystrokes
            _state.value = _state.value.copy(searching = true)
            _state.value = when (val result = chatRepository.searchUsers(query)) {
                is DataResult.Success -> _state.value.copy(results = result.data, searching = false)
                is DataResult.Failure -> _state.value.copy(
                    results = emptyList(),
                    searching = false,
                    signInRequired = result.error is AppError.Unauthorized,
                )
            }
        }
    }

    /** Start (or reopen) a DM with [profile]; on success the screen opens the thread. */
    fun startDm(profile: PublicProfile) {
        viewModelScope.launch {
            when (val result = chatRepository.ensureDmConversation(profile)) {
                is DataResult.Success -> _state.value = _state.value.copy(openConversationId = result.data)
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) _state.value = _state.value.copy(signInRequired = true)
            }
        }
    }

    /** Flip group-creation mode; leaving it drops the draft title/members. */
    fun toggleGroupMode() {
        _state.update {
            if (it.groupMode) it.copy(groupMode = false, groupTitle = "", groupMembers = emptyList())
            else it.copy(groupMode = true)
        }
    }

    fun setGroupTitle(title: String) {
        if (title.length <= 80) _state.update { it.copy(groupTitle = title) }
    }

    /** Add/remove a search result from the draft group (max 19 others + the creator = rules cap 20). */
    fun toggleGroupMember(profile: PublicProfile) {
        _state.update { current ->
            val existing = current.groupMembers.firstOrNull { it.uid == profile.uid }
            when {
                existing != null -> current.copy(groupMembers = current.groupMembers - existing)
                current.groupMembers.size >= 19 -> current
                else -> current.copy(groupMembers = current.groupMembers + profile)
            }
        }
    }

    /** Creates the group thread and opens it. Invitees receive it as a message request. */
    fun createGroup() {
        val current = _state.value
        if (current.groupTitle.isBlank() || current.groupMembers.isEmpty() || current.creatingGroup) return
        viewModelScope.launch {
            _state.update { it.copy(creatingGroup = true) }
            when (val result = chatRepository.createGroupConversation(current.groupTitle, current.groupMembers)) {
                is DataResult.Success -> _state.update { it.copy(openConversationId = result.data, creatingGroup = false) }
                is DataResult.Failure -> _state.update {
                    it.copy(
                        creatingGroup = false,
                        signInRequired = result.error is AppError.Unauthorized,
                    )
                }
            }
        }
    }

    fun consumeOpen() {
        _state.value = _state.value.copy(openConversationId = null)
    }
}

/** Find-your-people state: discoverable cards (intent-filtered) + the caller's own card. */
data class PeopleUiState(
    val loading: Boolean = true,
    val people: List<PublicProfile> = emptyList(),
    val intent: String? = null,
    val myCard: PublicProfile? = null,
    val signInRequired: Boolean = false,
    /** Set when Connect resolved a DM thread — the route navigates into it, then clears this. */
    val openConversationId: String? = null,
    /** Map fallback centre: the user's own fix, else the active metro's centre. */
    val mapCenter: diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint =
        diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint(43.6532, -79.3832),
)

/** People-map check-in: [busy] while saving; [error] when the last check-in or check-out failed. */
data class MapCheckInUiState(
    val busy: Boolean = false,
    val error: MapCheckInError? = null,
)

enum class MapCheckInError { NoLocation, Failed }

/** Drives the Find-your-people surface: opt-in directory + Connect (DM request) + card editor. */
@OptIn(ExperimentalCoroutinesApi::class)
class PeopleViewModel(
    private val chatRepository: ChatRepository,
    private val locationController: diana.soleil.design.iraniancanadienbazzar.core.data.LocationController,
    private val cityController: diana.soleil.design.iraniancanadienbazzar.core.data.CityController,
) : ViewModel() {

    /** One-shot platform location request (permission prompt at most once per session). */
    fun ensureLocation() { viewModelScope.launch { locationController.ensureLocation() } }

    private val intent = MutableStateFlow<String?>(null)
    private val myCard = MutableStateFlow<PublicProfile?>(null)
    private val gate = MutableStateFlow(false)
    private val openId = MutableStateFlow<String?>(null)

    val state: StateFlow<PeopleUiState> = combine(
        intent.flatMapLatest { chatRepository.observeDiscoverablePeople(it) },
        intent,
        myCard,
        gate,
        openId,
    ) { people, selectedIntent, card, signInRequired, open ->
        when (people) {
            is DataResult.Success -> PeopleUiState(
                loading = false,
                people = people.data,
                intent = selectedIntent,
                myCard = card,
                signInRequired = signInRequired,
                openConversationId = open,
                mapCenter = mapCenterPoint(),
            )
            is DataResult.Failure -> PeopleUiState(
                loading = false,
                intent = selectedIntent,
                myCard = card,
                signInRequired = signInRequired || people.error is AppError.Unauthorized,
                openConversationId = open,
            )
        }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), PeopleUiState())

    init {
        viewModelScope.launch {
            myCard.value = (chatRepository.myPublicProfile() as? DataResult.Success)?.data
        }
    }

    fun setIntent(value: String?) { intent.value = value }

    fun connect(person: PublicProfile) {
        viewModelScope.launch {
            when (val result = chatRepository.ensureDmConversation(person)) {
                is DataResult.Success -> openId.value = result.data
                is DataResult.Failure -> if (result.error is AppError.Unauthorized) gate.value = true
            }
        }
    }

    fun saveCard(discoverable: Boolean, bio: String?, intents: List<String>) {
        viewModelScope.launch {
            val saved = chatRepository.setDiscoveryProfile(discoverable, bio, intents)
            if (saved is DataResult.Failure) {
                if (saved.error is AppError.Unauthorized) gate.value = true
                return@launch
            }
            // Leaving Find your people also takes the member off the map straight away.
            if (!discoverable && myCard.value?.mapVisible == true) {
                chatRepository.setMapVisibility(
                    diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision.Hidden, null, null,
                )
            }
            myCard.value = (chatRepository.myPublicProfile() as? DataResult.Success)?.data
        }
    }

    private val _checkIn = MutableStateFlow(MapCheckInUiState())
    val checkIn: StateFlow<MapCheckInUiState> = _checkIn

    /**
     * Manual, time-boxed map check-in (App Store 5.1.2): the member taps to appear at an approximate
     * [precision] for 4 hours; nothing re-checks them in automatically. The map only lists discoverable
     * members, so checking in turns the list card on too (the sheet says so).
     */
    fun checkInOnMap(precision: diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision) {
        if (_checkIn.value.busy) return
        viewModelScope.launch {
            _checkIn.value = MapCheckInUiState(busy = true)
            locationController.ensureLocation()
            val fix = locationController.location.value
            if (fix == null) {
                _checkIn.value = MapCheckInUiState(error = MapCheckInError.NoLocation)
                return@launch
            }
            val card = myCard.value
            if (card?.discoverable != true) {
                val listed = chatRepository.setDiscoveryProfile(true, card?.bio, card?.intents.orEmpty())
                if (listed is DataResult.Failure) {
                    onCheckInFailure(listed.error)
                    return@launch
                }
            }
            val result = chatRepository.setMapVisibility(precision, fix.lat, fix.lng)
            if (result is DataResult.Failure) {
                onCheckInFailure(result.error)
                return@launch
            }
            myCard.value = (chatRepository.myPublicProfile() as? DataResult.Success)?.data
            _checkIn.value = MapCheckInUiState()
        }
    }

    /** Ends the caller's check-in now: pin and approximate area are removed. */
    fun leaveMap() {
        if (_checkIn.value.busy) return
        viewModelScope.launch {
            _checkIn.value = MapCheckInUiState(busy = true)
            val result = chatRepository.setMapVisibility(
                diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision.Hidden, null, null,
            )
            if (result is DataResult.Failure) {
                onCheckInFailure(result.error)
                return@launch
            }
            myCard.value = (chatRepository.myPublicProfile() as? DataResult.Success)?.data
            _checkIn.value = MapCheckInUiState()
        }
    }

    private fun onCheckInFailure(error: AppError) {
        if (error is AppError.Unauthorized) gate.value = true
        _checkIn.value = MapCheckInUiState(error = MapCheckInError.Failed)
    }

    fun consumeGate() { gate.value = false }
    fun consumeOpen() { openId.value = null }

    private fun mapCenterPoint(): diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint {
        locationController.location.value?.let {
            return diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint(it.lat, it.lng)
        }
        val metro = diana.soleil.design.iraniancanadienbazzar.core.model.Metros.byId(cityController.activeMetroId.value)
        return diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint(
            metro?.centerLat ?: 43.6532, metro?.centerLng ?: -79.3832,
        )
    }
}

/** Person-profile state: the member's public card, or not-found when they went private/deleted. */
data class PersonProfileUiState(
    val loading: Boolean = true,
    val person: PublicProfile? = null,
    /** True after a successful load that found no (or a non-discoverable) profile. */
    val notFound: Boolean = false,
    val signInRequired: Boolean = false,
    /** Set when Connect resolved a DM thread — the route navigates into it, then clears this. */
    val openConversationId: String? = null,
    val error: Boolean = false,
)

/** Loads one member's public profile ([uid]) and drives Connect (same DM-request flow as People). */
class PersonProfileViewModel(
    private val uid: String,
    private val chatRepository: ChatRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(PersonProfileUiState())
    val state: StateFlow<PersonProfileUiState> = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        _state.value = PersonProfileUiState()
        viewModelScope.launch {
            when (val result = chatRepository.publicProfile(uid)) {
                is DataResult.Success -> {
                    // A profile that opted out of discovery is only reachable via stale links —
                    // treat it as absent rather than exposing a card its owner switched off.
                    val person = result.data?.takeIf { it.discoverable }
                    _state.update { it.copy(loading = false, person = person, notFound = person == null) }
                }
                is DataResult.Failure -> _state.update {
                    it.copy(loading = false, error = result.error !is AppError.Unauthorized,
                        signInRequired = result.error is AppError.Unauthorized)
                }
            }
        }
    }

    fun connect() {
        val person = _state.value.person ?: return
        viewModelScope.launch {
            when (val result = chatRepository.ensureDmConversation(person)) {
                is DataResult.Success -> _state.update { it.copy(openConversationId = result.data) }
                is DataResult.Failure -> if (result.error is AppError.Unauthorized) {
                    _state.update { it.copy(signInRequired = true) }
                }
            }
        }
    }

    fun consumeGate() = _state.update { it.copy(signInRequired = false) }
    fun consumeOpen() = _state.update { it.copy(openConversationId = null) }
}

/** Channel screen state: the broadcast feed + the viewer's relationship to it (owner / follower). */
data class ChannelUiState(
    val loading: Boolean = true,
    /** Channel metadata; null once loaded = the owner hasn't started a channel yet. */
    val channel: diana.soleil.design.iraniancanadienbazzar.core.model.Channel? = null,
    val posts: List<diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost> = emptyList(),
    /** Business display name — resolves even before a channel doc exists (header + start screen). */
    val businessName: String = "",
    /** The signed-in viewer owns this business — shows the composer + "start channel" affordance. */
    val isOwner: Boolean = false,
    /** Following == channel membership (rides the follows relationship). */
    val isFollowing: Boolean = false,
    val posting: Boolean = false,
    val signInRequired: Boolean = false,
    val error: Boolean = false,
)

/**
 * Drives one business channel (`Route.Channel`): live posts, follow/unfollow membership, and the
 * owner-only broadcast composer (creating the channel doc on first post).
 */
class ChannelViewModel(
    private val businessId: String,
    private val channelRepository: diana.soleil.design.iraniancanadienbazzar.core.data.ChannelRepository,
    private val followRepository: diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository,
    private val businessRepository: BusinessRepository,
) : ViewModel() {

    private val business = MutableStateFlow<diana.soleil.design.iraniancanadienbazzar.core.model.Business?>(null)
    private val posting = MutableStateFlow(false)
    private val gate = MutableStateFlow(false)
    private val failed = MutableStateFlow(false)

    val state: StateFlow<ChannelUiState> = combine(
        channelRepository.observeChannel(businessId),
        channelRepository.observePosts(businessId),
        followRepository.observeFollowing(),
        business,
        combine(posting, gate, failed) { p, g, f -> Triple(p, g, f) },
    ) { channel, posts, following, biz, (isPosting, signInRequired, hasFailed) ->
        val channelValue = (channel as? DataResult.Success)?.data
        ChannelUiState(
            // combine only fires once every stream has emitted — the skeleton phase is over.
            loading = false,
            channel = channelValue,
            posts = (posts as? DataResult.Success)?.data.orEmpty(),
            businessName = channelValue?.name ?: biz?.name.orEmpty(),
            isOwner = channelRepository.currentUid != null &&
                (biz?.ownerUid ?: channelValue?.ownerUid) == channelRepository.currentUid,
            isFollowing = businessId in (following as? DataResult.Success)?.data.orEmpty(),
            posting = isPosting,
            signInRequired = signInRequired,
            error = hasFailed || channel is DataResult.Failure,
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ChannelUiState())

    init {
        load()
    }

    fun load() {
        failed.value = false
        viewModelScope.launch {
            business.value = (businessRepository.getBusiness(businessId) as? DataResult.Success)?.data
        }
    }

    /** Follow/unfollow the business (channel membership). Guests get the sign-in gate. */
    fun toggleFollow() {
        val follow = !state.value.isFollowing
        viewModelScope.launch {
            val result = followRepository.setFollowing(businessId, follow)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) gate.value = true
        }
    }

    /** Owner broadcast: creates the channel doc on first use, then publishes the post. */
    fun post(text: String) {
        val trimmed = text.trim()
        if (trimmed.isEmpty() || posting.value) return
        viewModelScope.launch {
            posting.value = true
            try {
                val biz = business.value
                if (state.value.channel == null && biz != null) {
                    val ensured = channelRepository.ensureChannel(biz)
                    if (ensured is DataResult.Failure) {
                        if (ensured.error is AppError.Unauthorized) gate.value = true else failed.value = true
                        return@launch
                    }
                }
                when (val result = channelRepository.post(businessId, trimmed)) {
                    is DataResult.Success -> Unit
                    is DataResult.Failure ->
                        if (result.error is AppError.Unauthorized) gate.value = true else failed.value = true
                }
            } finally {
                posting.value = false
            }
        }
    }

    fun consumeGate() { gate.value = false }
}
